// client/src/utils/timezone.js
import { toZonedTime, fromZonedTime, formatInTimeZone } from 'date-fns-tz'

// Detect the user's timezone
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone
const DEFAULT_FMT = 'h:mm a'

// Parse a Date or ISO string
const parseDate = (d) => (typeof d === 'string' ? new Date(d) : d)

/**
 * Convert a local Date → UTC Date
 */
export const toUTC = (localDate) => fromZonedTime(parseDate(localDate), TZ)

/**
 * Convert a UTC Date/string → local Date
 */
export const toLocal = (utcDate) => toZonedTime(parseDate(utcDate), TZ)

/**
 * Format any Date or string in the user's timezone
 */
export const formatLocal = (date, fmt = DEFAULT_FMT) =>
  formatInTimeZone(parseDate(date), TZ, fmt)

/**
 * Build an ISO string representing "local midnight+hour" in UTC
 */
export const createUTCFromLocalComponents = (
  year,
  month,
  day,
  hour,
  minute = 0,
) => {
  // this Date is interpreted as UTC → .toISOString() preserves it
  const d = new Date(Date.UTC(year, month, day, hour, minute))
  return d.toISOString()
}

/**
 * Create a "naive" datetime string that preserves the wall-clock time
 * This stores the time as if it were UTC, so it displays the same everywhere
 */
export const createNaiveDateTimeString = (
  year,
  month,
  day,
  hour,
  minute = 0,
) => {
  // Month is 0-based in JavaScript Date constructor
  const utcDate = new Date(Date.UTC(year, month, day, hour, minute))
  return utcDate.toISOString()
}

/**
 * Format a "naive" datetime string for display
 */
export const formatNaiveDateTime = (naiveDateString, fmt = 'h:mm a') => {
  if (!naiveDateString) return ''

  // Parse the date and format it as if it were local time
  const date = new Date(naiveDateString)

  // Use formatInTimeZone with UTC to show the "naive" time
  return formatInTimeZone(date, 'UTC', fmt)
}

/**
 * Parse a "naive" datetime string back to a Date object
 * This treats the stored UTC time as if it were local wall-clock time (no conversion)
 * Logs input and output for debugging timezone issues.
 */
export const parseNaiveDateTime = (naiveDateTimeString) => {
  if (!naiveDateTimeString) return null
  const dateObj = new Date(naiveDateTimeString)
  return dateObj
}

/**
 * Check if a UTC slot contains a given local date/hour/minute
 */
const slotContainsTime = (
  utcStart,
  utcEnd,
  localDate,
  localHour,
  localMinute = 0,
) => {
  const startLocal = toLocal(utcStart)
  const endLocal = toLocal(utcEnd)

  // anchor at the right hour and minute
  const check = new Date(localDate)
  check.setHours(localHour, localMinute, 0, 0)

  return startLocal <= check && check < endLocal
}

/**
 * Public API for your two use-cases:
 */
export const utcSlotContainsLocalTime = slotContainsTime

export const opponentSlotMatchesLocalTime = slotContainsTime

// Legacy exports for backward compatibility
export const convertLocalToUTC = toUTC
export const convertUTCToLocal = toLocal
export const getLocalHourFromUTC = (utcISOString) => {
  const localDate = toLocal(utcISOString)
  return localDate.getHours()
}
export const formatUTCAsLocal = (utcISOString, formatString = DEFAULT_FMT) => {
  return formatLocal(utcISOString, formatString)
}
